import { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMockAuth, usePermissions } from '../../hooks/useMockAuth';
import { useTheme } from '../../contexts/ThemeContext';

interface RootLayoutProps {
  children: ReactNode;
}

/**
 * RootLayout - Main application layout with top navigation
 * Shows role-appropriate navigation links based on user permissions
 */
export function RootLayout({ children }: RootLayoutProps) {
  const { user, isAuthenticated, logout } = useMockAuth();
  const { isAdmin } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  const colors = {
    dark: {
      navBg: '#1a1a2e',
      navBorder: '#2d2d44',
      footerBg: '#0f0f14',
      footerBorder: '#1a1a2e',
      text: '#ffffff',
      textMuted: '#d1d5db',
      hover: '#2d2d44'
    },
    light: {
      navBg: '#ffffff',
      navBorder: '#e5e7eb',
      footerBg: '#f9fafb',
      footerBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      hover: '#f3f4f6'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: theme === 'dark' ? '#0a0a0f' : '#ffffff' }}>
      {/* Top Navigation Bar */}
      <nav style={{
        backgroundColor: c.navBg,
        borderBottom: `1px solid ${c.navBorder}`,
        padding: '1rem 2rem'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo & Main Nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <Link
              to="/"
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                color: c.text,
                textDecoration: 'none',
                letterSpacing: '-0.02em'
              }}
            >
              patchGrid
            </Link>

            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <Link
                to="/marketplace"
                style={{
                  color: c.textMuted,
                  textDecoration: 'none',
                  fontSize: '1rem',
                  transition: 'color 0.2s'
                }}
                activeProps={{
                  style: { color: c.text, fontWeight: '500' }
                }}
              >
                Marketplace
              </Link>

              {isAuthenticated && (
                <Link
                  to="/creators"
                  style={{
                    color: c.textMuted,
                    textDecoration: 'none',
                    fontSize: '1rem',
                    transition: 'color 0.2s'
                  }}
                  activeProps={{
                    style: { color: c.text, fontWeight: '500' }
                  }}
                >
                  Creators
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/admin"
                  style={{
                    color: c.textMuted,
                    textDecoration: 'none',
                    fontSize: '1rem',
                    transition: 'color 0.2s'
                  }}
                  activeProps={{
                    style: { color: c.text, fontWeight: '500' }
                  }}
                >
                  Admin
                </Link>
              )}
            </div>
          </div>

          {/* User Menu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${c.navBorder}`,
                borderRadius: '0.5rem',
                padding: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = c.hover}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {isAuthenticated ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  paddingRight: '1rem',
                  borderRight: `1px solid ${c.navBorder}`
                }}>
                  <span style={{ color: c.textMuted, fontSize: '0.875rem' }}>
                    {user?.email}
                  </span>
                  <span style={{
                    backgroundColor: user?.role === 'admin' ? '#dc2626' :
                                   user?.role === 'creator' ? '#7c3aed' : '#2563eb',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {user?.role}
                  </span>
                </div>
                <Link
                  to="/profile"
                  style={{
                    color: c.textMuted,
                    textDecoration: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  style={{
                    color: c.textMuted,
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = c.hover}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  style={{
                    color: c.textMuted,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Login
                </Link>
                <Link
                  to="/login"
                  style={{
                    backgroundColor: '#667eea',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    fontWeight: '500'
                  }}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ flex: 1 }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: c.footerBg,
        color: c.textMuted,
        padding: '2rem',
        textAlign: 'center',
        borderTop: `1px solid ${c.footerBorder}`
      }}>
        <p style={{ margin: 0, fontSize: '0.875rem' }}>
          © 2025 patchGrid. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
